import { useEffect, useRef, useState } from "react";
import { ThemePicker } from "../components/ThemePicker";
import { api, ApiError } from "../lib/apiClient";
import type { IdentityUser } from "../lib/identity";
import type { Role, UserProfile } from "@shared/types";

const AVATAR_MIME_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_AVATAR_BYTES = 1.5 * 1024 * 1024;

/**
 * Takes user/role as props from AuthedApp (the same values Rail's account
 * menu already shows) rather than re-deriving them independently — an
 * earlier version called currentUser()/useCurrentAppUser() directly here
 * and showed a blank "—" for both on a fresh navigation, since App.tsx is
 * the one place that reliably resolves them into React state.
 *
 * onAvatarChange lets the Rail/UserMenu chrome (which reads the photo from
 * a separate useCurrentAppUser() instance in App.tsx) pick up a newly
 * uploaded or removed photo immediately, instead of waiting for the next
 * full navigation to re-fetch /api/me.
 */
export function SettingsPage({ user, role, onAvatarChange }: { user: IdentityUser; role: Role | null; onAvatarChange: () => void }) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [fullName, setFullName] = useState("");
  const [title, setTitle] = useState("");
  const [bio, setBio] = useState("");
  const [certifications, setCertifications] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .get<{ profile: UserProfile }>("/api/profile")
      .then((data) => {
        setProfile(data.profile);
        setFullName(data.profile.fullName ?? "");
        setTitle(data.profile.title ?? "");
        setBio(data.profile.bio ?? "");
        setCertifications(data.profile.certifications.join(", "));
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Failed to load your profile."))
      .finally(() => setLoading(false));
  }, []);

  function pickPhoto() {
    fileInputRef.current?.click();
  }

  function readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.slice(result.indexOf(",") + 1));
      };
      reader.onerror = () => reject(new Error("Couldn't read that file."));
      reader.readAsDataURL(file);
    });
  }

  async function handlePhotoSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setAvatarError(null);

    if (!AVATAR_MIME_TYPES.includes(file.type)) {
      setAvatarError("Please choose a PNG, JPEG, or WEBP image.");
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setAvatarError("Image is too large. Please use a photo under 1.5MB.");
      return;
    }

    setAvatarBusy(true);
    try {
      const data = await readFileAsBase64(file);
      const result = await api.put<{ avatarUrl: string }>("/api/profile/avatar", { mimeType: file.type, data });
      setProfile((p) => (p ? { ...p, avatarUrl: result.avatarUrl } : p));
      onAvatarChange();
    } catch (err) {
      setAvatarError(err instanceof ApiError ? err.message : "Failed to upload your photo.");
    } finally {
      setAvatarBusy(false);
    }
  }

  async function removePhoto() {
    setAvatarError(null);
    setAvatarBusy(true);
    try {
      await api.delete("/api/profile/avatar");
      setProfile((p) => (p ? { ...p, avatarUrl: null } : p));
      onAvatarChange();
    } catch (err) {
      setAvatarError(err instanceof ApiError ? err.message : "Failed to remove your photo.");
    } finally {
      setAvatarBusy(false);
    }
  }

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const data = await api.patch<{ profile: UserProfile }>("/api/profile", {
        fullName: fullName.trim(),
        title: title.trim(),
        bio: bio.trim(),
        certifications: certifications
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean),
      });
      setProfile(data.profile);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save your profile.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="content-col">
      <h1>Settings</h1>

      <section className="card settings-section">
        <h2 className="settings-section-title">Account</h2>
        <div className="settings-account-row">
          <span className="settings-account-label">Email</span>
          <span>{user.email}</span>
        </div>
        <div className="settings-account-row">
          <span className="settings-account-label">Role</span>
          <span style={{ textTransform: "capitalize" }}>{role ? role.replace(/_/g, " ") : "—"}</span>
        </div>
      </section>

      <section className="card settings-section">
        <h2 className="settings-section-title">Profile</h2>
        <p className="muted settings-section-desc">
          Shown to other Resident Engineers and Inspectors on the Network page — full details are visible once you're
          connected.
        </p>
        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            <div className="avatar-upload">
              <div className="avatar-upload-preview">
                {profile?.avatarUrl ? (
                  <img src={profile.avatarUrl} alt="" className="avatar-img" />
                ) : (
                  <svg width="26" height="26" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="5.5" r="2.75" stroke="currentColor" strokeWidth="1.3" />
                    <path d="M2.75 13.25c0-2.485 2.351-4.5 5.25-4.5s5.25 2.015 5.25 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                )}
              </div>
              <div className="avatar-upload-actions">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={AVATAR_MIME_TYPES.join(",")}
                  onChange={handlePhotoSelected}
                  style={{ display: "none" }}
                />
                <button type="button" className="btn" onClick={pickPhoto} disabled={avatarBusy}>
                  {avatarBusy ? "Working…" : profile?.avatarUrl ? "Change photo" : "Upload photo"}
                </button>
                {profile?.avatarUrl && (
                  <button type="button" className="btn" onClick={removePhoto} disabled={avatarBusy}>
                    Remove
                  </button>
                )}
                <span className="muted avatar-upload-hint">PNG, JPEG, or WEBP, up to 1.5MB.</span>
                {avatarError && <p className="error-text">{avatarError}</p>}
              </div>
            </div>
            <form onSubmit={saveProfile} className="profile-form">
            {error && <p className="error-text">{error}</p>}
            <label>
              Full name
              <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={profile?.email} />
            </label>
            <label>
              Title
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Senior Resident Engineer" />
            </label>
            <label>
              Bio
              <textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="A short professional summary…" />
            </label>
            <label>
              Certifications
              <input
                type="text"
                value={certifications}
                onChange={(e) => setCertifications(e.target.value)}
                placeholder="Comma-separated, e.g. ACI, NICET Level III, OSHA 30"
              />
            </label>
            <div>
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving ? "Saving…" : "Save profile"}
              </button>
              {saved && <span className="muted" style={{ marginLeft: "0.6rem" }}>Saved.</span>}
            </div>
            </form>
          </>
        )}
      </section>

      <section className="card settings-section">
        <h2 className="settings-section-title">Appearance</h2>
        <p className="muted settings-section-desc">Choose how RE Inspector Fieldbook looks on this device.</p>
        <ThemePicker />
      </section>
    </div>
  );
}
